import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HardHat, ArrowLeft, MessageSquare, Phone, Mail } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEO from '../components/SEO';

const SMSTermsPage = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="SMS Terms"
        description="ContractorsLab SMS Terms — how our optional text-message notifications work, opt-in, opt-out, frequency, and carrier rates."
        path="/sms-terms"
      />
      {/* Header */}
      <header className="bg-slate-900 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black text-white tracking-tight hidden sm:block">CONTRACTORSLAB</span>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="dark" />
              <Link to="/" className="text-slate-400 hover:text-white flex items-center gap-2 text-sm">
                <ArrowLeft className="w-4 h-4" /> {t('legal.backHome')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <article className="prose prose-slate max-w-none">
          <h1 className="text-3xl font-black text-slate-900 mb-2">{t('legal.smsTerms')}</h1>
          <p className="text-slate-500 mb-8">{t('legal.lastUpdated')}: January 19, 2026</p>

          <p className="lead text-lg text-slate-700">
            Terms and conditions for SMS messaging services provided through ContractorsLab.com.
            ContractorsLab.com is created, owned, and operated by <strong>Retention Logic LLC</strong>
            ("ContractorsLab," "we," "our," or "us").
          </p>

          {/* Quick Info Cards */}
          <div className="grid md:grid-cols-4 gap-4 my-8 not-prose">
            <div className="bg-slate-100 rounded-sm p-4 text-center">
              <p className="text-xs text-slate-500 uppercase font-bold">Short Code</p>
              <p className="text-lg font-bold text-slate-900">872504</p>
            </div>
            <div className="bg-slate-100 rounded-sm p-4 text-center">
              <p className="text-xs text-slate-500 uppercase font-bold">Program</p>
              <p className="text-lg font-bold text-slate-900">ContractorsLab</p>
            </div>
            <div className="bg-slate-100 rounded-sm p-4 text-center">
              <p className="text-xs text-slate-500 uppercase font-bold">Frequency</p>
              <p className="text-lg font-bold text-slate-900">Up to 10/month</p>
            </div>
            <div className="bg-slate-100 rounded-sm p-4 text-center">
              <p className="text-xs text-slate-500 uppercase font-bold">Pricing</p>
              <p className="text-lg font-bold text-slate-900">Msg & Data rates may apply</p>
            </div>
          </div>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">User Opt-In</h2>
          <p>
            By creating an account at ContractorsLab.com and opting in to SMS notifications, you consent to 
            receive SMS messages from ContractorsLab, including:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>For Clients:</strong> New proposal notifications, contractor selection confirmations, contact request alerts</li>
            <li><strong>For Contractors:</strong> New lead alerts, proposal status updates, wallet balance notifications, contact reveal confirmations</li>
            <li><strong>For All Users:</strong> Account security alerts, password reset codes, important service updates</li>
          </ul>

          <div className="bg-green-50 border border-green-200 rounded-sm p-4 my-6">
            <h3 className="text-green-800 font-bold mb-2">Important Privacy Notice</h3>
            <ul className="text-green-700 space-y-2 mb-0">
              <li>Messages are <strong>NOT</strong> sent using an automatic telephone dialing system (autodialer).</li>
              <li>Consent to receive SMS messages is <strong>NOT</strong> a condition of purchase or use of the ContractorsLab Service.</li>
              <li>Your phone number and all information collected during the SMS opt-in process will <strong>NOT</strong> be sold, rented, leased, or shared with third parties for marketing purposes.</li>
              <li>Your contact information is only shared with qualified Contractors you have explicitly selected.</li>
            </ul>
          </div>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">User Opt-Out</h2>
          <p>To stop receiving SMS messages at any time:</p>
          
          <div className="bg-orange-50 border border-orange-200 rounded-sm p-4 my-4 not-prose">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-8 h-8 text-orange-600" />
              <div>
                <p className="font-bold text-orange-800">Text "STOP" to 872504</p>
                <p className="text-sm text-orange-700">You may also reply with "QUIT," "END," "CANCEL," "UNSUBSCRIBE," or "STOP ALL"</p>
              </div>
            </div>
          </div>

          <p>
            Once you opt out, you will no longer receive SMS messages from ContractorsLab. You may also 
            disable SMS notifications in your account settings.
          </p>
          <p>
            <strong>Note:</strong> Opting out of SMS will not affect your ability to use ContractorsLab. 
            You will continue to receive notifications via email and in-app alerts.
          </p>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Help & Support</h2>
          <p>For assistance with SMS messaging:</p>
          
          <div className="grid md:grid-cols-3 gap-4 my-6 not-prose">
            <div className="bg-slate-100 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-5 h-5 text-slate-600" />
                <span className="font-bold text-slate-900">Text Help</span>
              </div>
              <p className="text-slate-600">Text "HELP" to 872504</p>
            </div>
            <div className="bg-slate-100 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-5 h-5 text-slate-600" />
                <span className="font-bold text-slate-900">Email</span>
              </div>
              <p className="text-slate-600">support@contractorslab.com</p>
            </div>
            <div className="bg-slate-100 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Phone className="w-5 h-5 text-slate-600" />
                <span className="font-bold text-slate-900">Phone</span>
              </div>
              <p className="text-slate-600">(877) 936-8897</p>
            </div>
          </div>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">User Fees</h2>
          <p>
            This is a standard-rate messaging service. <strong>Message and data rates may apply</strong> based 
            on your mobile carrier's plan. ContractorsLab does not charge any additional fees for SMS messages 
            beyond what your carrier may charge.
          </p>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Supported Carriers (U.S. Only)</h2>
          <p>Our SMS service is supported by most major U.S. carriers, including but not limited to:</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 my-4 not-prose">
            {['AT&T', 'T-Mobile', 'Verizon Wireless', 'US Cellular', 'Cricket', 
              'Metro by T-Mobile', 'Boost Mobile', 'Virgin Mobile', 'Straight Talk', 'Mint Mobile'].map((carrier) => (
              <span key={carrier} className="bg-slate-100 text-slate-700 px-3 py-2 rounded text-sm text-center">
                {carrier}
              </span>
            ))}
          </div>
          <p className="text-sm text-slate-500">
            Carriers are not responsible for delayed or undelivered messages.
          </p>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Types of Messages</h2>
          <p>When you opt in to SMS notifications, you may receive the following types of messages:</p>
          
          <div className="overflow-x-auto my-6">
            <table className="min-w-full border border-slate-200">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Message Type</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Frequency</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr>
                  <td className="px-4 py-3 font-medium">New Lead Alerts</td>
                  <td className="px-4 py-3 text-slate-600">Notifications when you're matched with a new job (Contractors)</td>
                  <td className="px-4 py-3 text-slate-600">As leads are matched</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-4 py-3 font-medium">Proposal Updates</td>
                  <td className="px-4 py-3 text-slate-600">Confirmations when proposals are submitted or selected</td>
                  <td className="px-4 py-3 text-slate-600">As needed</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Contact Requests</td>
                  <td className="px-4 py-3 text-slate-600">Alerts when a Contractor requests contact info (Clients)</td>
                  <td className="px-4 py-3 text-slate-600">As requested</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-4 py-3 font-medium">Wallet Alerts</td>
                  <td className="px-4 py-3 text-slate-600">Low balance warnings and top-up confirmations (Contractors)</td>
                  <td className="px-4 py-3 text-slate-600">As needed</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Security Alerts</td>
                  <td className="px-4 py-3 text-slate-600">Password reset codes, login alerts, account changes</td>
                  <td className="px-4 py-3 text-slate-600">As needed</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-4 py-3 font-medium">Service Updates</td>
                  <td className="px-4 py-3 text-slate-600">Important service announcements and changes</td>
                  <td className="px-4 py-3 text-slate-600">Occasional</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Warranty Disclaimer</h2>
          <p>
            ContractorsLab is not responsible for any delays in SMS delivery, as delivery depends on your 
            wireless carrier's network. We make reasonable efforts to deliver messages promptly, but cannot 
            guarantee delivery times or delivery at all due to factors beyond our control.
          </p>
          <p className="mt-4">
            <strong>You acknowledge and agree that SMS notifications are a convenience feature and should not 
            be your sole method of tracking job leads, proposals, or important deadlines.</strong> We recommend 
            also checking your dashboard and email for important notifications.
          </p>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Privacy & Data Protection</h2>
          <p>
            Your privacy is important to us. The phone number and information you provide for SMS services is 
            used <strong>ONLY</strong> to deliver requested communications and manage your account.
          </p>
          
          <div className="bg-slate-100 rounded-sm p-4 my-4">
            <h3 className="font-bold text-slate-900 mb-2">We will NEVER:</h3>
            <ul className="list-disc pl-6 text-slate-700 space-y-1 mb-0">
              <li>Sell your phone number to third parties</li>
              <li>Rent or lease your contact information</li>
              <li>Share your number with marketers or advertisers</li>
              <li>Use your number for purposes other than ContractorsLab services</li>
            </ul>
          </div>

          <p>
            We may disclose information only when legally required (e.g., by court order) or when necessary 
            to protect our rights or property. For more information, please review our full{' '}
            <Link to="/privacy" className="text-orange-600 hover:text-orange-700">Privacy Policy</Link>.
          </p>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Contact Information Sharing via SMS</h2>
          <p>
            If you receive a notification that your contact information has been shared, this means:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>A Client has explicitly approved sharing their contact info with a selected Contractor</li>
            <li>OR a Contractor's contact info has been revealed to a Client they submitted a proposal to</li>
          </ul>
          <p className="mt-4">
            Contact information is <strong>ONLY</strong> shared between matched, qualified parties who have 
            both consented to the connection. Your information is never shared with:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Third-party marketers</li>
            <li>Data brokers</li>
            <li>Advertising networks</li>
            <li>Unrelated businesses</li>
          </ul>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Dispute Resolution</h2>
          <p>
            All disputes arising from these SMS Terms will be resolved through <strong>binding arbitration</strong> in 
            accordance with the American Arbitration Association's rules, as described in our{' '}
            <Link to="/terms" className="text-orange-600 hover:text-orange-700">Terms of Service</Link>.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Arbitration will be conducted by a single arbitrator, whose decision is final and binding</li>
            <li>Both parties waive the right to participate in any class action or jury trial</li>
            <li>Each party will bear their own costs unless the arbitrator awards fees to the prevailing party</li>
          </ul>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Governing Law and Jurisdiction</h2>
          <p>
            These SMS Terms are governed by and construed in accordance with the laws of the
            <strong> Commonwealth of Puerto Rico</strong> and applicable U.S. federal laws
            (including the Telephone Consumer Protection Act ("TCPA"), CAN-SPAM, CTIA messaging
            guidelines, and FCC regulations), without regard to conflict-of-laws principles. For
            any matter not subject to arbitration, the parties submit to the exclusive jurisdiction
            of the Commonwealth Court of First Instance of Puerto Rico (Tribunal de Primera
            Instancia) and the United States District Court for the District of Puerto Rico, both
            sitting in San Juan, Puerto Rico.
          </p>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Miscellaneous</h2>
          <p>
            These SMS Terms, together with our{' '}
            <Link to="/terms" className="text-orange-600 hover:text-orange-700">Terms of Service</Link> and{' '}
            <Link to="/privacy" className="text-orange-600 hover:text-orange-700">Privacy Policy</Link>, 
            form the entire agreement between you and ContractorsLab regarding SMS communications.
          </p>
          <p className="mt-4">
            Updates or modifications to these SMS Terms will be posted at contractorslab.com/sms-terms and are 
            effective immediately unless otherwise noted. Your continued use of SMS services constitutes 
            acceptance of any updated Terms.
          </p>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Contact Us</h2>
          <p>For any questions about these SMS Terms:</p>
          <div className="bg-slate-100 rounded-sm p-4 mt-4">
            <p><strong>ContractorsLab</strong></p>
            <p>Email: support@contractorslab.com</p>
            <p>Phone: (877) 936-8897</p>
            <p className="mt-2 text-sm text-slate-600">
              To opt out of SMS: Text STOP to 872504
            </p>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              By opting in to SMS notifications from ContractorsLab, you acknowledge that you have read, 
              understood, and agree to be bound by these SMS Terms.
            </p>
          </div>
        </article>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 py-8">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="font-black text-white">CONTRACTORSLAB</span>
            </div>
            <div className="flex items-center gap-6 text-slate-400 text-sm">
              <Link to="/terms" className="hover:text-white">Terms</Link>
              <Link to="/privacy" className="hover:text-white">Privacy</Link>
              <Link to="/sms-terms" className="hover:text-white">SMS Terms</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SMSTermsPage;
